import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { WeeklyInsightResponse } from '../types';
import { insightsApiService } from '../services/insightsApi';
import { isWeeklyInsightPopupDay } from './insightsEligibility';

// Weekend-scoped so a dismissal earlier in the same week (legacy `insights_popup_viewed_*`
// flags) doesn't suppress the weekend nudge. Both weekend days map to the same week start,
// so dismissing on Saturday still suppresses Sunday.
const POPUP_KEY_PREFIX = 'insights_weekend_viewed_';

function getWeekStartDate(): string {
  const today = new Date();
  const daysFromMonday = ((today.getDay() - 1 + 7) % 7);
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - daysFromMonday);
  return weekStart.toISOString().split('T')[0];
}

interface InsightsState {
  weeklyInsight: WeeklyInsightResponse | null;
  isLoading: boolean;
  isGenerating: boolean;
  error: string | null;

  loadWeeklyInsights: () => Promise<void>;
  generateInsights: () => Promise<void>;
  clearError: () => void;
  shouldShowInsightsPopup: (accountCreatedAt: string) => boolean;
  markInsightsPopupViewed: () => void;
}

export const useInsightsStore = create<InsightsState>()(
  devtools(
    (set) => ({
      weeklyInsight: null,
      isLoading: false,
      isGenerating: false,
      error: null,

      loadWeeklyInsights: async () => {
        set({ isLoading: true, error: null });
        try {
          const insight = await insightsApiService.getWeeklyInsights();
          set({ weeklyInsight: insight, isLoading: false });
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to load weekly insights', isLoading: false });
        }
      },

      generateInsights: async () => {
        set({ isGenerating: true, error: null });
        try {
          const insight = await insightsApiService.generateWeeklyInsights();
          set({ weeklyInsight: insight, isGenerating: false });
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to generate weekly insights', isGenerating: false });
        }
      },

      clearError: () => set({ error: null }),

      shouldShowInsightsPopup: (accountCreatedAt: string) => {
        // Weekend-only, and only once the account has a full week of history behind it.
        if (!isWeeklyInsightPopupDay(new Date(), accountCreatedAt)) return false;
        const key = `${POPUP_KEY_PREFIX}${getWeekStartDate()}`;
        return !localStorage.getItem(key);
      },

      markInsightsPopupViewed: () => {
        const key = `${POPUP_KEY_PREFIX}${getWeekStartDate()}`;
        localStorage.setItem(key, 'true');
      },
    }),
    { name: 'insights-store' }
  )
);
